import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

interface ForwardEndpoint {
  id: string;
  name: string;
  url: string;
  is_active: boolean;
  timeout_ms: number;
  bot_id: string;
  created_at: string;
  updated_at: string;
}

interface EndpointFormData {
  name: string;
  url: string;
  timeout_ms: number;
}

interface EndpointManagementProps {
  filters: {
    botId: string | null;
    instrument: string | null;
    dateFrom: string | null;
    dateTo: string | null;
  };
  refreshKey: number;
}

export function EndpointManagement({ filters, refreshKey }: EndpointManagementProps) {
  const [endpoints, setEndpoints] = useState<ForwardEndpoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<ForwardEndpoint | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<EndpointFormData>({
    name: '',
    url: '',
    timeout_ms: 5000,
  });

  useEffect(() => {
    fetchEndpoints();
  }, [filters.botId, refreshKey]);

  const fetchEndpoints = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('bot_signal_forward_endpoints')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by bot if selected
      if (filters.botId) {
        query = query.eq('bot_id', filters.botId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching endpoints:', error);
        toast.error('Failed to load endpoints');
        return;
      }

      setEndpoints(data || []);
    } catch (error) {
      console.error('Error fetching endpoints:', error);
      toast.error('Failed to load endpoints');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.url) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!filters.botId) {
      toast.error('Please select a bot first');
      return;
    }

    // Validate URL
    try {
      new URL(formData.url);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        name: formData.name,
        url: formData.url,
        timeout_ms: formData.timeout_ms,
        bot_id: filters.botId,
        is_active: true
      };

      if (editingEndpoint) {
        // Update existing endpoint
        const { error } = await supabase
          .from('bot_signal_forward_endpoints')
          .update(payload)
          .eq('id', editingEndpoint.id);

        if (error) {
          console.error('Error updating endpoint:', error);
          toast.error('Failed to update endpoint');
          return;
        }

        toast.success('Endpoint updated successfully');
      } else {
        // Create new endpoint
        const { error } = await supabase
          .from('bot_signal_forward_endpoints')
          .insert([payload]);

        if (error) {
          console.error('Error creating endpoint:', error);
          toast.error('Failed to create endpoint');
          return;
        }

        toast.success('Endpoint created successfully');
      }

      // Reset form and close dialog
      setFormData({ name: '', url: '', timeout_ms: 5000 });
      setEditingEndpoint(null);
      setIsDialogOpen(false);
      fetchEndpoints();
    } catch (error) {
      console.error('Error saving endpoint:', error);
      toast.error('Failed to save endpoint');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (endpoint: ForwardEndpoint) => {
    try {
      const { error } = await supabase
        .from('bot_signal_forward_endpoints')
        .update({ is_active: !endpoint.is_active })
        .eq('id', endpoint.id);

      if (error) {
        console.error('Error toggling endpoint:', error);
        toast.error('Failed to update endpoint status');
        return;
      }

      toast.success(`Endpoint ${!endpoint.is_active ? 'enabled' : 'disabled'}`);
      fetchEndpoints();
    } catch (error) {
      console.error('Error toggling endpoint:', error);
      toast.error('Failed to update endpoint status');
    }
  };

  const handleDelete = async (endpoint: ForwardEndpoint) => {
    if (!confirm(`Are you sure you want to delete endpoint "${endpoint.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('bot_signal_forward_endpoints')
        .delete()
        .eq('id', endpoint.id);

      if (error) {
        console.error('Error deleting endpoint:', error);
        toast.error('Failed to delete endpoint');
        return;
      }

      toast.success('Endpoint deleted successfully');
      fetchEndpoints();
    } catch (error) {
      console.error('Error deleting endpoint:', error);
      toast.error('Failed to delete endpoint');
    }
  };

  const handleEdit = (endpoint: ForwardEndpoint) => {
    setEditingEndpoint(endpoint);
    setFormData({
      name: endpoint.name,
      url: endpoint.url,
      timeout_ms: endpoint.timeout_ms,
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingEndpoint(null);
    setFormData({ name: '', url: '', timeout_ms: 5000 });
    setIsDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Forward Endpoints</CardTitle>
              <CardDescription>
                Manage endpoints that receive webhook payloads in parallel
              </CardDescription>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={handleAddNew}
                  disabled={!filters.botId}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Endpoint
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>
                      {editingEndpoint ? 'Edit Endpoint' : 'Add New Endpoint'}
                    </DialogTitle>
                    <DialogDescription>
                      Configure an endpoint to receive webhook payloads
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Production API"
                        required
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="url">URL</Label>
                      <Input
                        id="url"
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://api.example.com/webhook"
                        required
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="timeout">Timeout (ms)</Label>
                      <Input
                        id="timeout"
                        type="number"
                        min="1000"
                        max="30000"
                        step="1000"
                        value={formData.timeout_ms}
                        onChange={(e) => setFormData(prev => ({ ...prev, timeout_ms: parseInt(e.target.value) }))}
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {editingEndpoint ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            </div>
            {!filters.botId && (
              <p className="text-sm text-muted-foreground">
                Please select a bot to manage endpoints
              </p>
            )}
        </CardHeader>
      </Card>

      {/* Endpoints Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading endpoints...</div>
            </div>
          ) : !filters.botId ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Select a bot to view endpoints</div>
            </div>
          ) : endpoints.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">No endpoints configured for this bot</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Timeout</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints.map((endpoint) => (
                  <TableRow key={endpoint.id}>
                    <TableCell className="font-medium">
                      {endpoint.name}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        {endpoint.url}
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                        >
                          <a 
                            href={endpoint.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={endpoint.is_active ? 'default' : 'secondary'}>
                          {endpoint.is_active ? (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 mr-1" />
                              Disabled
                            </>
                          )}
                        </Badge>
                        <Switch
                          checked={endpoint.is_active}
                          onCheckedChange={() => handleToggleActive(endpoint)}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {endpoint.timeout_ms}ms
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(endpoint.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(endpoint)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(endpoint)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}